import type { PocInputValidity } from 'commons/types';
import { useCallback, useMemo, useState } from 'react';
import {
    ActiveDirectoryDetailsFormState,
    ConnectionStatus,
    TestConnectionState,
} from 'modules/settings/pods/settings-integrations/settings-integrations-active-directory/types/active-directory.type';
import { ActiveDirectoryDetailsItem } from 'modules/settings/pods/settings-integrations/settings-integrations-active-directory/components/settings-integrations-active-directory-details/active-directory-details-item.vm';
import {
    ConnectionFormFieldErrors,
    ConnectionFormFieldName,
    getConnectionErrorMessage,
    getConnectionStatus,
    isValidConnectionFormState,
} from './active-directory-connection-details-form.business';
import { getFieldValidity } from './active-directory-connection-details-form.validation';

interface UseConnectionDetailsFormProps {
    isDirty: boolean;
    isEditMode: boolean;
    activeDirectory?: ActiveDirectoryDetailsItem;
    formState: ActiveDirectoryDetailsFormState;
    testConnectionState: TestConnectionState;
    fieldErrors: ConnectionFormFieldErrors;
    onChangeField: (field: string, value: string) => void;
    onTestConnection: () => Promise<void>;
    onEditConnectionDetails: VoidFunction;
}

interface UseConnectionDetailsFormReturn {
    editable: boolean;
    canTestConnection: boolean;
    currentConnectionStatus: ConnectionStatus;
    currentConnectionErrorMessage: string;
    lastSyncDate: string | null;
    lastSyncHour: string | null;
    computeFieldValidity: (field: ConnectionFormFieldName) => PocInputValidity | undefined;
    handleBlur: (field: ConnectionFormFieldName) => void;
    handleChangeField: (field: string, value: string | null) => void;
    handleActivateEditMode: () => void;
    handleTestConnection: () => void;
}

export const useConnectionDetailsForm = ({
    isDirty,
    isEditMode,
    activeDirectory,
    formState,
    testConnectionState,
    fieldErrors,
    onChangeField,
    onTestConnection,
    onEditConnectionDetails,
}: UseConnectionDetailsFormProps): UseConnectionDetailsFormReturn => {
    const [editable, setEditable] = useState(!isEditMode);
    const [touched, setTouched] = useState<Set<ConnectionFormFieldName>>(new Set());

    const handleBlur = useCallback((field: ConnectionFormFieldName) => {
        setTouched((prev) => {
            if (prev.has(field)) {
                return prev;
            }
            const next = new Set(prev);
            next.add(field);
            return next;
        });
    }, []);

    const computeFieldValidity = useCallback(
        (field: ConnectionFormFieldName) =>
            getFieldValidity(
                field,
                formState,
                touched,
                fieldErrors,
                editable ? false : activeDirectory?.hasSavedPassword
            ),
        [touched, formState, fieldErrors, editable, activeDirectory?.hasSavedPassword]
    );

    const currentConnectionStatus = useMemo(
        () =>
            getConnectionStatus({
                isDirty,
                testConnectionState,
                activeDirectoryStatus: activeDirectory?.status,
            }),
        [isDirty, testConnectionState, activeDirectory]
    );

    const currentConnectionErrorMessage = useMemo(
        () =>
            getConnectionErrorMessage({
                isEditMode,
                isDirty,
                testConnectionState,
                errorConnectionDetail: activeDirectory?.errorConnectionDetail,
            }),
        [isEditMode, isDirty, testConnectionState, activeDirectory]
    );

    const canTestConnection = isValidConnectionFormState(
        formState,
        editable ? false : Boolean(activeDirectory?.hasSavedPassword)
    );

    const connectionLastSync = isEditMode ? activeDirectory?.lastSync : null;
    const lastSyncDate = connectionLastSync ? new Date(connectionLastSync).toLocaleDateString() : null;
    const lastSyncHour = connectionLastSync ? new Date(connectionLastSync).toLocaleTimeString() : null;

    const handleChangeField = useCallback(
        (field: string, value: string | null) => {
            onChangeField(field, value ?? '');
        },
        [onChangeField]
    );

    const handleActivateEditMode = useCallback(() => {
        setEditable(true);
        onEditConnectionDetails();
    }, [onEditConnectionDetails]);

    const handleTestConnection = useCallback(() => {
        setEditable(false);
        onTestConnection().catch(() => {
            setEditable(true);
        });
    }, [onTestConnection]);

    return {
        editable,
        canTestConnection,
        currentConnectionStatus,
        currentConnectionErrorMessage,
        lastSyncDate,
        lastSyncHour,
        computeFieldValidity,
        handleBlur,
        handleChangeField,
        handleActivateEditMode,
        handleTestConnection,
    };
};
